'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { Icon, IconComponent } from '@graftcode/design-system'
import clsx from 'clsx'

import {
  type GraftcodeOnboardingStackOption,
  shouldUseFullWidthStackPicker,
} from '../../graftcode-onboarding-stacks.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'

import styles from './GraftcodeOnboardingDashboard.module.scss'

type GraftcodeOnboardingStackPickerProps = {
  stacks: GraftcodeOnboardingStackOption[]
  selectedStackId: string
  onSelect: (stackId: string) => void
  onPrefetchStack?: (stackId: string) => void
  ariaLabel: string
  scrollBackLabel: string
  scrollForwardLabel: string
}

const SCROLL_STEP_PX = 160
const SCROLL_EDGE_PX = 2

export function GraftcodeOnboardingStackPicker({
  stacks,
  selectedStackId,
  onSelect,
  onPrefetchStack,
  ariaLabel,
  scrollBackLabel,
  scrollForwardLabel,
}: GraftcodeOnboardingStackPickerProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollBack, setCanScrollBack] = useState(false)
  const [canScrollForward, setCanScrollForward] = useState(false)

  const updateScrollState = useCallback(() => {
    const track = trackRef.current
    if (!track) {
      setCanScrollBack(false)
      setCanScrollForward(false)
      return
    }
    setCanScrollBack(track.scrollLeft > SCROLL_EDGE_PX)
    setCanScrollForward(track.scrollLeft + track.clientWidth < track.scrollWidth - SCROLL_EDGE_PX)
  }, [])

  useEffect(() => {
    updateScrollState()
    const track = trackRef.current
    if (!track) {
      return undefined
    }

    track.addEventListener('scroll', updateScrollState, { passive: true })
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(track)

    return () => {
      track.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [stacks, updateScrollState])

  const scrollByStep = (direction: -1 | 1) => {
    trackRef.current?.scrollBy({ left: direction * SCROLL_STEP_PX, behavior: 'smooth' })
    window.setTimeout(updateScrollState, 300)
  }

  const fullWidthScroller = shouldUseFullWidthStackPicker(stacks.length)

  return (
    <div
      className={clsx(
        styles.stackScroller,
        !fullWidthScroller && styles.stackScrollerHug,
        canScrollBack && styles.stackScrollerHasBack,
        canScrollForward && styles.stackScrollerHasForward
      )}
      data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.stackPicker}
    >
      {canScrollBack ? (
        <>
          <div className={styles.stackScrollerFadeStart} aria-hidden />
          <button
            type="button"
            className={clsx(styles.stackNavButton, styles.stackNavButtonBack)}
            aria-label={scrollBackLabel}
            onClick={() => scrollByStep(-1)}
          >
            <IconComponent icon={Icon.ChevronLeftRegular} size={16} />
          </button>
        </>
      ) : null}
      <div ref={trackRef} className={styles.stackTrack} role="group" aria-label={ariaLabel}>
        {stacks.map((stack) => {
          const active = stack.id === selectedStackId
          return (
            <button
              key={stack.id}
              type="button"
              className={clsx(styles.stackCard, active && styles.stackCardActive)}
              aria-pressed={active}
              onClick={() => onSelect(stack.id)}
              onMouseEnter={() => onPrefetchStack?.(stack.id)}
              onFocus={() => onPrefetchStack?.(stack.id)}
            >
              <span className={styles.stackLogo}>
                {stack.icon ? <IconComponent icon={stack.icon} size={20} /> : null}
              </span>
              <span className={styles.stackLabel}>{stack.label}</span>
            </button>
          )
        })}
      </div>
      {canScrollForward ? (
        <>
          <div className={styles.stackScrollerFade} aria-hidden />
          <button
            type="button"
            className={styles.stackNavButton}
            aria-label={scrollForwardLabel}
            onClick={() => scrollByStep(1)}
          >
            <IconComponent icon={Icon.ChevronRightRegular} size={16} />
          </button>
        </>
      ) : null}
    </div>
  )
}
